// ============================================================================
// HR PIN HEADER - Ant Design Header Component
// Path: frontend/src/components/hr-pin/HrPinHeader.tsx
// ============================================================================

import React, { useMemo } from 'react';
import { 
  Layout, 
  Avatar, 
  Dropdown, 
  Typography, 
  Space, 
  Badge,
  Button,
  Tooltip
} from 'antd';
import { 
  UserOutlined, 
  LogoutOutlined, 
  SettingOutlined,
  BellOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { AuthUser } from '../../services/auth.service';
import styles from './HrPinHeader.module.css';

const { Header } = Layout;
const { Text } = Typography;

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface HrPinHeaderProps {
  user: AuthUser;
  onLogout: () => void;
  sidebarCollapsed: boolean;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const HrPinHeader: React.FC<HrPinHeaderProps> = ({ user, onLogout, sidebarCollapsed }) => {
  // Get user initials for avatar
  const userInitials = useMemo(() => {
    const name = user.employeeName || user.name || 'HR';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, [user]);

  // Dropdown menu items
  const dropdownItems: MenuProps['items'] = useMemo(() => [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      disabled: true,
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: onLogout,
    },
  ], [onLogout]);

  return (
    <Header className={styles.header}>
      <div className={styles.headerContent}>
        {/* Left Section - Logo & Title */}
        <div className={styles.leftSection}>
          <div className={styles.logoContainer}>
            <img 
              src={`${process.env.PUBLIC_URL}/images/photos.png`}
              alt="Phoenician Logo"
              className={styles.logo}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <div className={styles.titleContainer}>
              <Text className={styles.title}>Phoenician HR Portal</Text>
              <Text className={styles.subtitle}>HR Management System</Text>
            </div>
          </div>
        </div>

        {/* Right Section - Actions & User */}
        <div className={styles.rightSection}>
          {/* Help Button */}
          <Tooltip title="Help & Support">
            <Button 
              type="text" 
              icon={<QuestionCircleOutlined />} 
              className={styles.headerButton}
            />
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <Badge count={0} size="small">
              <Button 
                type="text" 
                icon={<BellOutlined />} 
                className={styles.headerButton}
              />
            </Badge>
          </Tooltip>

          {/* User Dropdown */}
          <Dropdown
            menu={{ items: dropdownItems }}
            placement="bottomRight"
            trigger={['click']}
            overlayClassName={styles.dropdownOverlay}
          >
            <div className={styles.userSection}>
              <div className={styles.userInfo}>
                <Text className={styles.userName}>
                  {user.employeeName || user.name || 'HR Manager'}
                </Text>
                <Text className={styles.userRole}>
                  {user.designation || 'HR & Office Manager'}
                </Text>
              </div>
              <Avatar 
                size={40} 
                className={styles.avatar}
                style={{ 
                  backgroundColor: '#4A90E2',
                  cursor: 'pointer'
                }}
              >
                {userInitials}
              </Avatar>
            </div>
          </Dropdown>
        </div>
      </div>
    </Header>
  );
};

export default HrPinHeader;